import type { components } from './schema';

/**
 * El contrato de error del backend, del lado del cliente.
 *
 * Todos los errores de `api/ManejadorDeErrores.java` tienen la misma forma:
 * `{"error": "<código>", "detalle": "<texto>"}`.
 *
 * ⚠️ REGLA QUE NO SE NEGOCIA: se decide sobre `error`, NUNCA sobre `detalle`.
 * El código es contrato estable; el detalle está escrito para una persona y
 * puede cambiar de redacción sin aviso. Un `if (e.detalle.includes('...'))` se
 * rompe el día que alguien corrige una tilde.
 */

/**
 * Códigos que produce el backend. NO están escritos a mano: salen del `enum`
 * del esquema `Error` en `docs/openapi.yaml`, vía los tipos generados. Si el
 * backend agrega o renombra un código, esta unión cambia sola al regenerar.
 */
export type CodigoErrorApi = components['schemas']['Error']['error'];

/**
 * Códigos que pone el cliente, para las fallas que nunca llegan a tener un
 * cuerpo de error del backend. Van aparte a propósito: quien los lea tiene que
 * poder distinguir "el servicio dijo que no" de "no pudimos ni preguntar".
 */
export const CODIGOS_LOCALES = {
  /** No hubo respuesta: red caída, backend apagado, DNS, timeout del browser. */
  sinConexion: 'sin_conexion',
  /** Hubo respuesta de error, pero su cuerpo no es el contrato del facturador. */
  respuestaIlegible: 'respuesta_ilegible',
  /**
   * 403 de Spring Security (`/api/v1/admin/**` sin rol ADMIN). Lo produce la
   * cadena de seguridad por fuera de `ManejadorDeErrores`, así que su cuerpo es
   * el genérico de Spring Boot (`{"error":"Forbidden",...}`) y no trae ningún
   * código estable. Ver el esquema `ErrorSpringBoot` del OpenAPI.
   */
  prohibido: 'prohibido',
} as const;

export type CodigoErrorLocal = (typeof CODIGOS_LOCALES)[keyof typeof CODIGOS_LOCALES];

/** Cualquier código con el que puede llegar un {@link ErrorApi}. */
export type CodigoError = CodigoErrorApi | CodigoErrorLocal;

/** Campo → mensaje de validación. Solo viene en algunos `request_invalida`. */
export type CamposInvalidos = Readonly<Record<string, string>>;

/**
 * Error tipado de la API. Es lo único que lanza el cliente HTTP: quien llama
 * atrapa esto y hace `switch (e.codigo)`.
 */
export class ErrorApi extends Error {
  /** Código estable sobre el que se decide. */
  readonly codigo: CodigoError;
  /** Texto para humanos. Se puede MOSTRAR; no se parsea ni se compara. */
  readonly detalle: string;
  /** Status HTTP, o `0` si nunca hubo respuesta. */
  readonly estado: number;
  /** Campos rechazados por Bean Validation. Vacío si el error no los trae. */
  readonly campos: CamposInvalidos;
  /**
   * `true` si el código lo mandó el backend; `false` si lo dedujo el cliente
   * (ver {@link CODIGOS_LOCALES}).
   */
  readonly delBackend: boolean;
  /** La excepción original, cuando la hubo (falla de red). */
  override readonly cause?: unknown;

  constructor(datos: {
    codigo: CodigoError;
    detalle: string;
    estado: number;
    campos?: CamposInvalidos;
    delBackend: boolean;
    cause?: unknown;
  }) {
    super(`${datos.codigo}: ${datos.detalle}`);
    this.name = 'ErrorApi';
    this.codigo = datos.codigo;
    this.detalle = datos.detalle;
    this.estado = datos.estado;
    this.campos = datos.campos ?? {};
    this.delBackend = datos.delBackend;
    if (datos.cause !== undefined) {
      this.cause = datos.cause;
    }
  }

  /** Falla sin respuesta: el pedido no llegó o no volvió. */
  static deRed(causa: unknown): ErrorApi {
    return new ErrorApi({
      codigo: CODIGOS_LOCALES.sinConexion,
      detalle: 'No se pudo contactar al servicio',
      estado: 0,
      delBackend: false,
      cause: causa,
    });
  }

  /**
   * Traduce una respuesta HTTP de error.
   *
   * Reconoce el contrato del facturador por su FORMA (`error` y `detalle`,
   * ambos strings), no por una lista de códigos copiada acá: duplicar la lista
   * sería volver a escribir a mano lo que se genera desde el contrato, y el día
   * que el backend agregue un código el cliente lo degradaría a "ilegible" en
   * vez de pasarlo. Un código nuevo llega tal cual; lo que no tiene la forma
   * del contrato (el 403 de Spring Security, un HTML de proxy) cae en un código
   * local.
   */
  static deRespuesta(estado: number, cuerpo: unknown): ErrorApi {
    const contrato = leerCuerpoDeError(cuerpo);
    if (contrato) {
      return new ErrorApi({
        codigo: contrato.codigo,
        detalle: contrato.detalle,
        estado,
        campos: contrato.campos,
        delBackend: true,
      });
    }
    if (estado === 403) {
      return new ErrorApi({
        codigo: CODIGOS_LOCALES.prohibido,
        detalle: 'No tenés permiso para esta operación',
        estado,
        delBackend: false,
      });
    }
    return new ErrorApi({
      codigo: CODIGOS_LOCALES.respuestaIlegible,
      detalle: `El servicio respondió ${estado} con un cuerpo inesperado`,
      estado,
      delBackend: false,
    });
  }
}

/** `true` si `e` es un {@link ErrorApi}. Para el `catch (e: unknown)`. */
export function esErrorApi(e: unknown): e is ErrorApi {
  return e instanceof ErrorApi;
}

/** `true` si `e` es un {@link ErrorApi} con alguno de esos códigos. */
export function tieneCodigo(e: unknown, ...codigos: readonly CodigoError[]): boolean {
  return esErrorApi(e) && codigos.includes(e.codigo);
}

/**
 * El mensaje de validación de UN campo, o `null` si el error no lo menciona.
 *
 * Viene del mapa `campos` que trae el `400 request_invalida` de Bean Validation,
 * con las claves del DTO del backend. Se muestra **pegado al campo**: un cartel
 * genérico arriba obliga a adivinar cuál de los campos está mal, que es justo el
 * dato que el backend acaba de contestar.
 *
 * Vivía en `usuarios/mensajes.ts` hasta KAN-144. Se levantó acá **recién con el
 * segundo ABM escrito** —el criterio que dejó KAN-143— porque no tiene nada de
 * usuarios ni de sistemas: es leer el mapa del contrato. Lo que sigue siendo de
 * cada módulo es el texto de cada código, que es donde las dos pantallas dicen
 * cosas distintas.
 */
export function mensajeDeCampo(error: unknown, campo: string): string | null {
  if (!esErrorApi(error)) {
    return null;
  }
  return error.campos[campo] ?? null;
}

interface CuerpoDeError {
  codigo: CodigoError;
  detalle: string;
  campos: CamposInvalidos;
}

/**
 * Interpreta el cuerpo JSON de un error del facturador. Devuelve `null` si el
 * cuerpo no tiene la forma del contrato.
 */
function leerCuerpoDeError(cuerpo: unknown): CuerpoDeError | null {
  if (typeof cuerpo !== 'object' || cuerpo === null) {
    return null;
  }
  const objeto = cuerpo as Record<string, unknown>;
  const codigo = objeto['error'];
  const detalle = objeto['detalle'];
  if (typeof codigo !== 'string' || typeof detalle !== 'string') {
    return null;
  }
  return {
    // Único lugar con una aserción de tipo: el backend podría mandar un código
    // que todavía no está en el contrato generado. Se pasa tal cual en vez de
    // perderlo — ver el comentario de `deRespuesta`.
    codigo: codigo as CodigoError,
    detalle,
    campos: leerCampos(objeto['campos']),
  };
}

function leerCampos(campos: unknown): CamposInvalidos {
  if (typeof campos !== 'object' || campos === null) {
    return {};
  }
  const salida: Record<string, string> = {};
  for (const [nombre, mensaje] of Object.entries(campos)) {
    if (typeof mensaje === 'string') {
      salida[nombre] = mensaje;
    }
  }
  return salida;
}
